import { SearchParams } from '@/constants/listing-search-keys';
import { ListingQueryProps } from 'types/listing-query-props';

export const DEFAULT_SEARCH_ADULTS = 2;
export const DEFAULT_SEARCH_CHILDREN = 0;

export type GuestCounts = {
  adults: number;
  children: number;
};

export type GuestSearchDefaults = {
  adults: number;
  children: number;
};

const defaultGuestSearchDefaults: GuestSearchDefaults = {
  adults: DEFAULT_SEARCH_ADULTS,
  children: DEFAULT_SEARCH_CHILDREN,
};

const parseCount = (value: string | null | undefined, fallback: number) => {
  if (value == null || value === '') return fallback;
  const parsed = parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : fallback;
};

/** Read adults/children from URL params for UI state. Ignores legacy `guests`. */
export const parseGuestCountsFromSearchParams = (
  searchParams: URLSearchParams,
  defaults: GuestSearchDefaults = defaultGuestSearchDefaults
): GuestCounts => {
  const adults = searchParams.has(SearchParams.ADULTS)
    ? parseCount(searchParams.get(SearchParams.ADULTS), defaults.adults)
    : defaults.adults;

  const children = searchParams.has(SearchParams.CHILDREN)
    ? parseCount(searchParams.get(SearchParams.CHILDREN), defaults.children)
    : defaults.children;

  return { adults, children };
};

/** Read adults/children from listing page query props. Legacy `guests` maps to adults only. */
export const parseGuestCountsFromListingQuery = (
  searchParams: Pick<ListingQueryProps, 'adults' | 'children' | 'guests'>,
  defaults: GuestSearchDefaults = defaultGuestSearchDefaults
): GuestCounts => {
  const adults = searchParams.adults
    ? parseCount(searchParams.adults, defaults.adults)
    : searchParams.guests
      ? parseCount(searchParams.guests, defaults.adults)
      : defaults.adults;

  const children = searchParams.children
    ? parseCount(searchParams.children, defaults.children)
    : defaults.children;

  return { adults, children };
};

export const totalGuestsFromCounts = ({ adults, children }: GuestCounts) =>
  adults + children;

export const hasExplicitGuestSearchParams = (
  searchParams: Pick<ListingQueryProps, 'adults' | 'children' | 'guests'>
) =>
  searchParams.guests != null ||
  searchParams.adults != null ||
  searchParams.children != null;

export const hasExplicitAdultsOrChildrenSearchParams = (
  searchParams: Pick<ListingQueryProps, 'adults' | 'children'> | URLSearchParams
) => {
  if (searchParams instanceof URLSearchParams) {
    return (
      searchParams.has(SearchParams.ADULTS) ||
      searchParams.has(SearchParams.CHILDREN)
    );
  }

  return searchParams.adults != null || searchParams.children != null;
};

export type ApplyGuestCountsOptions = {
  /** When true, default counts are written to the URL (e.g. adults=2) instead of omitted. */
  explicitDefaults?: boolean;
};

/** Write adults/children to URL params and remove legacy `guests`. */
export const applyGuestCountsToSearchParams = (
  current: URLSearchParams,
  adults: number,
  children: number,
  defaults: GuestSearchDefaults = defaultGuestSearchDefaults,
  options: ApplyGuestCountsOptions = {}
) => {
  current.delete(SearchParams.GUESTS);

  const isDefaultAdults = adults === defaults.adults;
  const isDefaultChildren = children === defaults.children;

  if (isDefaultAdults && isDefaultChildren) {
    if (options.explicitDefaults) {
      current.set(SearchParams.ADULTS, adults.toString());
      current.delete(SearchParams.CHILDREN);
    } else {
      current.delete(SearchParams.ADULTS);
      current.delete(SearchParams.CHILDREN);
    }
    return;
  }

  // When children is set, adults must also be present even at the default count.
  current.set(SearchParams.ADULTS, adults.toString());

  if (!isDefaultChildren) {
    current.set(SearchParams.CHILDREN, children.toString());
  } else {
    current.delete(SearchParams.CHILDREN);
  }
};

/** Remove all guest-related search params (including legacy `guests`). */
export const clearGuestSearchParams = (current: URLSearchParams) => {
  current.delete(SearchParams.GUESTS);
  current.delete(SearchParams.ADULTS);
  current.delete(SearchParams.CHILDREN);
};
